import { Link, Outlet, useLocation } from '@tanstack/react-router';
import {
  AlertTriangle,
  Building2,
  CircleDollarSign,
  FileCog,
  Flag,
  Gauge,
  History,
  Key,
  KeyRound,
  Monitor,
  Package,
  Shield,
  Users,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

import { useFeatureGate } from '~/hooks/useFeatureGate';
import { useAuthStore } from '~/store/auth.store';
import { isSystemSubsidiaryAdmin } from '~/utils/access';
import { cn } from '~/utils/cn';

interface PlatformNavItem {
  to: string;
  label: string;
  icon: LucideIcon;
}

// TODO: Restore the `Data Management` entry (import/export config) once the backend
// fully supports the data-management APIs. See docs/changes/2026-04-23-hide-data-management-and-impersonate.md.
const platformNavItems: PlatformNavItem[] = [
  {
    to: '/platform/users',
    label: 'Users',
    icon: Users,
  },
  {
    to: '/platform/subsidiaries',
    label: 'Subsidiaries',
    icon: Building2,
  },
  {
    to: '/platform/permissions',
    label: 'Permissions',
    icon: Key,
  },
  {
    to: '/platform/resources',
    label: 'Resources',
    icon: Shield,
  },
  {
    to: '/platform/screens',
    label: 'Screens',
    icon: Monitor,
  },
  {
    to: '/platform/service-auth',
    label: 'Service Auth',
    icon: KeyRound,
  },
  {
    to: '/platform/products',
    label: 'Products',
    icon: Package,
  },
  {
    to: '/platform/feature-flags',
    label: 'Feature Flags',
    icon: Flag,
  },
  {
    to: '/platform/enrichment-limit',
    label: 'Enrichment Limits',
    icon: Gauge,
  },
  {
    to: '/platform/cost-approval',
    label: 'Cost Approval',
    icon: CircleDollarSign,
  },
  {
    to: '/platform/transformers',
    label: 'File Transformers',
    icon: FileCog,
  },
  {
    to: '/platform/audit-logs',
    label: 'Audit Logs',
    icon: History,
  },
];

/** Failed batch review — appended only for Lily AI platform admins with the `batch-review` flag on. */
const batchReviewNavItem: PlatformNavItem = {
  to: '/platform/batch-review',
  label: 'Batch Review',
  icon: AlertTriangle,
};

/** Layout shell for the Platform Administration section — card nav + content outlet. */
export function PlatformLayout() {
  const location = useLocation();
  const { isAllowed: batchReviewFlag } = useFeatureGate('batch-review');
  const isPlatformAdmin = useAuthStore((s) => isSystemSubsidiaryAdmin(s.user));
  // Same predicate as the route guard, so the card never links to a page that redirects.
  const showBatchReview = batchReviewFlag && isPlatformAdmin;
  const navItems = [...platformNavItems, ...(showBatchReview ? [batchReviewNavItem] : [])];

  // Full-screen pages bypass the two-column layout: onboarding wizard,
  // subsidiary product-enrollment detail pages, the three-panel transformer
  // editor and the wide Failed batch review table + detail panel need the full content area.
  // /platform/schedules and /platform/coverage only redirect to their top-level pages.
  const isFullScreen =
    location.pathname.includes('/onboarding') ||
    location.pathname.startsWith('/platform/batch-review') ||
    location.pathname.startsWith('/platform/schedules') ||
    location.pathname.startsWith('/platform/coverage') ||
    /\/subsidiaries\/[^/]+\/products/.test(location.pathname) ||
    /\/platform\/transformers\/[^/]+/.test(location.pathname);
  if (isFullScreen) {
    return <Outlet />;
  }

  return (
    <div className="bg-gradient-page flex h-full flex-col gap-8 p-6">
      {/* Page header */}
      <div className="flex flex-col gap-2">
        <h1 className="text-foreground text-2xl leading-tight font-semibold">
          Platform Administration
        </h1>
        <p className="text-muted-foreground text-sm leading-5">
          Manage platform-wide users, permissions, resources, screens, feature flags, and tenant
          configuration.
        </p>
      </div>

      {/* Two-column layout: card nav + content */}
      <div className="flex min-h-0 flex-1 gap-4">
        {/* Card nav menu */}
        <aside className="bg-card flex w-[28%] max-w-80 min-w-[200px] shrink-0 flex-col gap-1 self-start rounded-2xl p-4 shadow-sm">
          {navItems.map((item) => (
            <PlatformNavLink key={item.to} item={item} />
          ))}
        </aside>

        {/* Inner page content */}
        <div className="min-w-0 flex-1 overflow-auto">
          <Outlet />
        </div>
      </div>
    </div>
  );
}

/** Single nav item — highlights active route with brand accent, keeps icon. */
function PlatformNavLink({ item }: { item: PlatformNavItem }) {
  const location = useLocation();
  const isActive = location.pathname.startsWith(item.to);
  const Icon = item.icon;

  return (
    <Link
      to={item.to}
      className={cn(
        'flex cursor-pointer items-center gap-2.5 rounded-xl px-4 py-3 text-sm font-medium transition-colors',
        isActive
          ? 'bg-accent-main text-white'
          : 'text-muted-foreground hover:bg-muted hover:text-foreground',
      )}
    >
      <Icon className="h-4 w-4 shrink-0" />
      <span>{item.label}</span>
    </Link>
  );
}
